"use client";

import { IImagePopulated } from "@/types/images.types";
import { cn } from "@/utils/cn";
import React, { useMemo, useState } from "react";
import FallbackIcon from "@/images/fallback.webp";

interface ResponsiveImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  images: IImagePopulated[];
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  objectFit?: "cover" | "contain";
  maxSize?: "mobile" | "tablet" | "desktop" | "large" | "original";
}

const SIZE_ORDER = [
  "mobile",
  "tablet",
  "desktop",
  "large",
  "original",
] as const;
type DerivedSize = (typeof SIZE_ORDER)[number];
type ImageSize = DerivedSize | "original";

const MEDIA_QUERIES: Record<DerivedSize, string> = {
  mobile: "(max-width: 320px)",
  tablet: "(max-width: 640px)",
  desktop: "(max-width: 1200px)",
  large: "(min-width: 1201px)",
};

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  images = [],
  alt,
  className,
  loading = "lazy",
  objectFit = "cover",
  maxSize = "large",
  ...rest
}) => {
  const [sizeError, setSizeError] = useState<string | null>(maxSize);

  console.log("IMAGES", images);

  const imagesByType = useMemo(() => {
    return images.reduce<Record<ImageSize, IImagePopulated[]>>(
      (acc, img) => {
        if (!acc[img.type as ImageSize]) acc[img.type as ImageSize] = [];
        acc[img.type as ImageSize].push(img);
        return acc;
      },
      {} as Record<ImageSize, IImagePopulated[]>,
    );
  }, [images]);

  const allowedDerivedSizes = useMemo<DerivedSize[]>(() => {
    const maxIndex = SIZE_ORDER.indexOf(sizeError);
    return SIZE_ORDER.slice(0, maxIndex + 1);
  }, [sizeError]);

  const getSourcesForType = (type: ImageSize) => {
    const imgs = imagesByType[type] || [];
    return imgs.map((img) => (
      <source
        key={img.url}
        srcSet={encodeURI(img.url)}
        type={`image/${img.format}`}
        media={MEDIA_QUERIES[img.type]}
      />
    ));
  };

  const getMobileSources = () => {
    const imgs = imagesByType["mobile"] || [];
    return imgs.map((img) => (
      <source
        key={encodeURI(img.url)}
        srcSet={img.url}
        type={`image/${img.format}`}
      />
    ));
  };

  const getTabletSources = () => {
    const imgs = imagesByType["tablet"] || [];
    return imgs.map((img) => (
      <source
        key={encodeURI(img.url)}
        srcSet={img.url}
        type={`image/${img.format}`}
      />
    ));
  };

  if (!sizeError) {
    return (
      <img
        src={FallbackIcon.src}
        alt={alt}
        loading={loading}
        className="w-full h-full object-contain"
        {...rest}
      />
    );
  }

  const currentSrc =
    imagesByType[sizeError as ImageSize]?.[0]?.url ?? FallbackIcon.src;

  return (
    <picture className={cn(className)}>
      {sizeError && (
        <>
          {sizeError === "mobile" && getMobileSources()}
          {sizeError === "tablet" && getTabletSources()}
          {sizeError !== "mobile" &&
            sizeError !== "tablet" &&
            allowedDerivedSizes.map((size) => {
              return getSourcesForType(size);
            })}
          {imagesByType.original && getSourcesForType("original")}
        </>
      )}
      <img
        src={currentSrc}
        alt={alt}
        loading={loading}
        className="w-full h-full"
        style={{ objectFit }}
        onError={(e) => {
          // setHasError(true)
          console.log("error", sizeError);
          const index = SIZE_ORDER.findIndex((item) => item === sizeError);
          if (index !== -1) {
            const nextSize = SIZE_ORDER[index + 1];
            if (nextSize) {
              setSizeError(nextSize);
            } else {
              setSizeError(null);
            }
            // e.currentTarget.onerror = null;
            // return;
          } else {
            setSizeError(null);
          }
          // setSizeError(null);
          // e.currentTarget.onerror = null;
        }}
        {...rest}
      />
    </picture>
  );
};

export default ResponsiveImage;
